import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useReveal, useCounter } from '../hooks/useReveal';
import Marquee from '../components/Marquee';
import {
  Globe, Shield, Headphones, BarChart2, Smartphone, ArrowRight, Star, ChevronDown, ChevronUp,
  Code, Palette, Server, RefreshCw, TrendingUp
} from 'lucide-react';
const words = ['Restaurants', 'Clinics', 'Schools', 'Shops', 'Salons', 'NGOs'];

// ─── HERO ────────────────────────────────────────────────────────────────────
function Hero({ width }) {
  const [typed, setTyped] = useState('');
  const [wi, setWi] = useState(0);
  const [ci, setCi] = useState(0);
  const [del, setDel] = useState(false);

  useEffect(() => {
    const cur = words[wi];
    const t = setTimeout(() => {
      if (!del) {
        setTyped(cur.slice(0, ci + 1));
        if (ci + 1 === cur.length) setTimeout(() => setDel(true), 1600);
        else setCi(c => c + 1);
      } else {
        setTyped(cur.slice(0, ci - 1));
        if (ci - 1 === 0) { setDel(false); setWi(i => (i + 1) % words.length); setCi(0); }
        else setCi(c => c - 1);
      }
    }, del ? 50 : 90);
    return () => clearTimeout(t);
  }, [ci, del, wi]);

  return (
    <section style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', textAlign: 'center', padding: '200px 24px 80px', position: 'relative', overflow: 'hidden' }}>
      {/* Background */}
      <div style={{ position: 'absolute', inset: 0, pointerEvents: 'none' }}>
        <div style={{ position: 'absolute', width: '700px', height: '700px', background: 'radial-gradient(circle, rgba(120,81,160,0.06) 0%, transparent 65%)', top: '50%', left: '50%', transform: 'translate(-50%,-50%)' }} />
        <div style={{ position: 'absolute', inset: 0, backgroundImage: 'linear-gradient(rgba(0,0,0,0.04) 1px, transparent 1px), linear-gradient(90deg,rgba(0,0,0,0.04) 1px,transparent 1px)', backgroundSize: '60px 60px' }} />
      </div>

      <div style={{ position: 'relative', maxWidth: '900px', margin: '0 auto' }}>
        {/* Badge */}
        <div style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', padding: '7px 18px', background: 'rgba(120,81,160,0.07)', border: '1px solid rgba(120,81,160,0.2)', borderRadius: '20px', fontSize: '12px', color: 'var(--lime-dark)', marginBottom: '28px', fontWeight: 600, animation: 'fadeUp 0.6s ease both' }}>
          <div style={{ width: '7px', height: '7px', borderRadius: '50%', background: 'var(--lime)', animation: 'pulse 2s infinite' }} />
          Pakistan's Simplest Website Subscription
        </div>

        {/* Headline */}
        <h1 style={{ fontFamily: 'Syne,sans-serif', fontSize: 'clamp(42px,7vw,82px)', fontWeight: 800, lineHeight: 1.05, letterSpacing: '-2px', marginBottom: '20px', animation: 'fadeUp 0.6s 0.1s ease both' }}>
          Websites for<br />
          <span className="lime-text">
            {typed}<span style={{ borderRight: '3px solid var(--lime)', animation: 'blink 1s infinite', marginLeft: '2px' }}></span>
          </span>
          <br />across Pakistan
        </h1>

        <p style={{ fontSize: 'clamp(15px,2vw,18px)', color: 'var(--text2)', maxWidth: '560px', margin: '0 auto 36px', lineHeight: 1.8, animation: 'fadeUp 0.6s 0.2s ease both' }}>
          Get a professional website with hosting & maintenance for just <strong style={{ color: 'var(--lime-dark)' }}>Rs. 5,000/month</strong>. No upfront cost. No tech headaches. Just results.
        </p>

        <div style={{ display: 'flex', gap: '12px', justifyContent: 'center', flexWrap: 'wrap', marginBottom: '56px', animation: 'fadeUp 0.6s 0.3s ease both' }}>
          <Link to="/pricing" className="btn-lime" style={{ fontSize: '15px', padding: '15px 32px' }}>
            Get Started — Rs. 5,000/mo <ArrowRight size={16} />
          </Link>
          <a href="#services" className="btn-outline" style={{ fontSize: '15px', padding: '15px 32px' }}
            onClick={e => { e.preventDefault(); document.getElementById('services')?.scrollIntoView({ behavior: 'smooth' }); }}>
            See What's Included
          </a>
        </div>

        {/* Trust badges */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '28px', flexWrap: 'wrap', animation: 'fadeUp 0.6s 0.4s ease both' }}>
          {[
            { icon: '✓', text: 'No upfront cost' },
            { icon: '✓', text: 'Cancel anytime' },
            { icon: '✓', text: 'Live in 7 days' },
            { icon: '✓', text: 'Pakistani support' },
          ].map((b, i) => (
            <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', color: 'var(--text2)' }}>
              <span style={{ color: 'var(--lime-dark)', fontWeight: 700 }}>{b.icon}</span> {b.text}
            </div>
          ))}
        </div>
      </div>

      {/* Scroll indicator */}
      <div style={{ position: 'absolute', bottom: '36px', left: width > 767 ? '50%' : '44%', transform: width > 767 ? 'translateX(-50%)' : 'none', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px', animation: 'float 2.5s ease infinite' }}>
        <div style={{ width: '1px', height: '48px', background: 'linear-gradient(to bottom, var(--lime), transparent)' }} />
        <div style={{ fontSize: '10px', letterSpacing: '3px', textTransform: 'uppercase', color: 'var(--text3)' }}>scroll</div>
      </div>
    </section>
  );
}

// ─── STATS ────────────────────────────────────────────────────────────────────
function Stats({ width }) {
  const c1 = useCounter(500);
  const c2 = useCounter(7);
  const c3 = useCounter(98);
  const c4 = useCounter(5000);

  const items = [
    { ref: c1, suffix: '+', label: 'Businesses Online', sublabel: 'across Pakistan' },
    { ref: c2, suffix: ' days', label: 'Average Launch Time', sublabel: 'from signup to live' },
    { ref: c3, suffix: '%', label: 'Client Retention', sublabel: 'month over month' },
    { ref: c4, suffix: '/mo', prefix: 'Rs. ', label: 'All-in Price', sublabel: 'website + hosting + support' },
  ];

  return (
    <section style={{ padding: '80px 24px', background: 'var(--bg2)', borderTop: '1px solid var(--border)', borderBottom: '1px solid var(--border)' }}>
      <div style={{ maxWidth: '1140px', margin: '0 auto', display: 'grid', gridTemplateColumns: width > 1024 ? 'repeat(4, 1fr)' : width > 640 ? 'repeat(2, 1fr)' : '1fr', gap: width > 640 ? '24px' : '16px' }}>
        {items.map((s, i) => (
          <div key={i} className="reveal" style={{ textAlign: 'center', transitionDelay: `${i * 0.1}s` }}>
            <div style={{ fontFamily: 'Syne,sans-serif', fontSize: width > 640 ? 'clamp(32px,4vw,52px)' : 'clamp(24px,6vw,36px)', fontWeight: 800, color: 'var(--lime-dark)', marginBottom: '6px', letterSpacing: width > 640 ? '-2px' : '-1px' }}>
              {s.prefix || ''}<span ref={s.ref}>0</span>{s.suffix}
            </div>
            <div style={{ fontSize: width > 640 ? '15px' : '13px', fontWeight: 600, color: 'var(--text)', marginBottom: '3px' }}>{s.label}</div>
            <div style={{ fontSize: width > 640 ? '12px' : '11px', color: 'var(--text3)' }}>{s.sublabel}</div>
          </div>
        ))}
      </div>
    </section>
  );
}

// ─── PROBLEM / HOOK ───────────────────────────────────────────────────────────
function Problem({ width }) {
  return (
    <section className="section" style={{ background: 'var(--bg)' }}>
      <div className="container">
        <div style={{ display: 'grid', gridTemplateColumns: width > 768 ? '1fr 1fr' : '1fr', gap: width > 768 ? '80px' : '40px', alignItems: width > 768 ? 'center' : 'stretch' }}>
          <div>
            <div className="label reveal">The Problem</div>
            <h2 className="syne reveal" style={{ fontSize: 'clamp(28px,4vw,48px)', fontWeight: 800, lineHeight: 1.1, letterSpacing: '-1.5px', marginBottom: '20px' }}>
              Thousands of Pakistani<br />businesses have <span className="lime-text">no website.</span>
            </h2>
            <p className="reveal" style={{ fontSize: '15px', color: 'var(--text2)', lineHeight: 1.85, marginBottom: '24px' }}>
              They either think it's too expensive, too complicated, or don't know where to start. Meanwhile their competitors are getting customers online every single day.
            </p>
            <p className="reveal" style={{ fontSize: '15px', color: 'var(--text2)', lineHeight: 1.85 }}>
              <strong style={{ color: 'var(--text)' }}>WebWalay fixes this.</strong> We handle everything — design, development, hosting and monthly updates — so you never have to worry about any of it.
            </p>
          </div>

          <div className="reveal-right">
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              {[
                { icon: '😩', title: 'Web agencies charge Rs. 50,000–200,000 upfront', bad: true },
                { icon: '😕', title: 'Freelancers disappear after delivery', bad: true },
                { icon: '🤯', title: 'WordPress is confusing to manage yourself', bad: true },
                { icon: '✅', title: 'WebWalay: Pay Rs. 5,000/mo, we handle everything', bad: false },
              ].map((item, i) => (
                <div key={i} style={{
                  display: 'flex', alignItems: 'center', gap: '14px',
                  padding: '16px 20px',
                  background: item.bad ? 'var(--bg3)' : 'rgba(120,81,160,0.05)',
                  border: `1px solid ${item.bad ? 'var(--border)' : 'rgba(120,81,160,0.2)'}`,
                  borderRadius: '12px',
                  animationDelay: `${i * 0.1}s`,
                }}>
                  <span style={{ fontSize: '22px', flexShrink: 0 }}>{item.icon}</span>
                  <span style={{ fontSize: '14px', color: item.bad ? 'var(--text2)' : 'var(--lime-dark)', fontWeight: item.bad ? 400 : 600, textDecoration: item.bad ? 'line-through' : 'none', textDecorationColor: 'var(--text3)' }}>
                    {item.title}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

// ─── SERVICES ────────────────────────────────────────────────────────────────
function Services({ width }) {
  const services = [
    { icon: <Globe size={24} />, title: 'Professional Website', desc: 'Custom-designed, mobile-first website that looks great on every device and loads lightning fast.', color: '#7851a0' },
    { icon: <Server size={24} />, title: 'Hosting Included', desc: 'Reliable, secure Pakistani hosting with 99.9% uptime guarantee. No separate billing.', color: '#7c6cfa' },
    { icon: <RefreshCw size={24} />, title: 'Monthly Updates', desc: 'Need to change text, add photos, or update business hours? We do it same day, no extra charge.', color: '#f59e0b' },
    { icon: <Shield size={24} />, title: 'SSL & Security', desc: 'Free SSL certificate for trust and SEO. Daily backups and security monitoring included.', color: '#10b981' },
    { icon: <Smartphone size={24} />, title: 'Mobile Optimised', desc: 'Over 70% of Pakistani internet users are on mobile. Your site will look perfect on all screens.', color: '#ec4899' },
    { icon: <Headphones size={24} />, title: 'Pakistani Support', desc: 'WhatsApp and call support in Urdu and English. Real humans, not bots, available 6 days a week.', color: '#3b82f6' },
    { icon: <BarChart2 size={24} />, title: 'Google Analytics', desc: 'See how many people visit your website, where they come from, and what they look at.', color: '#f97316' },
    { icon: <TrendingUp size={24} />, title: 'Basic SEO Setup', desc: 'We configure your site so Google can find and rank it. More visibility = more customers.', color: '#a855f7' },
  ];

  return (
    <section id="services" className="section" style={{ background: 'var(--bg2)' }}>
      <div className="container">
        <div style={{ textAlign: 'center', marginBottom: '60px' }}>
          <div className="label reveal" style={{ justifyContent: 'center' }}>What You Get</div>
          <h2 className="syne reveal" style={{ fontSize: 'clamp(28px,4vw,52px)', fontWeight: 800, letterSpacing: '-1.5px', marginBottom: '16px' }}>
            Everything included.<br /><span className="lime-text">Zero extras.</span>
          </h2>
          <p className="reveal" style={{ fontSize: '15px', color: 'var(--text2)', maxWidth: '520px', margin: '0 auto', lineHeight: 1.8 }}>
            One flat monthly fee covers everything you need to have a professional online presence.
          </p>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: width > 1024 ? 'repeat(4, 1fr)' : width > 640 ? 'repeat(2, 1fr)' : '1fr', gap: width > 640 ? '16px' : '12px' }}>
          {services.map((s, i) => (
            <div key={i} className="card reveal" style={{ padding: '24px', transitionDelay: `${i * 0.07}s`, cursor: 'default' }}>
              <div style={{ width: '48px', height: '48px', borderRadius: '12px', background: `${s.color}15`, border: `1px solid ${s.color}30`, display: 'flex', alignItems: 'center', justifyContent: 'center', color: s.color, marginBottom: '16px' }}>
                {s.icon}
              </div>
              <h3 style={{ fontFamily: 'Syne,sans-serif', fontSize: '15px', fontWeight: 700, color: 'var(--text)', marginBottom: '8px' }}>{s.title}</h3>
              <p style={{ fontSize: '13px', color: 'var(--text2)', lineHeight: 1.7 }}>{s.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ─── PROCESS ─────────────────────────────────────────────────────────────────
function Process({ width }) {
  const steps = [
    { num: '01', icon: <Code size={22} />, title: 'Sign Up Online', desc: 'Fill our simple form. Tell us about your business, what you do, and any content you want on the site.' },
    { num: '02', icon: <Palette size={22} />, title: 'We Design', desc: 'Our team creates a custom design tailored to your brand and industry. You review and approve it.' },
    { num: '03', icon: <Globe size={22} />, title: 'We Build & Launch', desc: 'We develop, test, and deploy your website. Usually live within 7 working days of your approval.' },
    { num: '04', icon: <RefreshCw size={22} />, title: 'We Maintain', desc: 'Pay Rs. 5,000 every month and we keep your site fast, secure, updated and running perfectly.' },
  ];

  return (
    <section id="process" className="section" style={{ background: 'var(--bg)' }}>
      <div className="container">
        <div style={{ textAlign: 'center', marginBottom: '60px' }}>
          <div className="label reveal" style={{ justifyContent: 'center' }}>How It Works</div>
          <h2 className="syne reveal" style={{ fontSize: 'clamp(28px,4vw,52px)', fontWeight: 800, letterSpacing: '-1.5px', marginBottom: '16px' }}>
            From idea to live website<br /><span className="lime-text">in 4 simple steps.</span>
          </h2>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: width > 1024 ? 'repeat(4, 1fr)' : width > 640 ? 'repeat(2, 1fr)' : '1fr', gap: '24px', position: 'relative' }}>
          {/* Connecting line - only show on desktop */}
          {width > 1024 && (
            <div style={{ position: 'absolute', top: '36px', left: '12.5%', right: '12.5%', height: '1px', background: 'linear-gradient(90deg, transparent, var(--lime), var(--lime), transparent)', zIndex: 0 }} />
          )}

          {steps.map((s, i) => (
            <div key={i} className="reveal" style={{ textAlign: 'center', position: 'relative', transitionDelay: `${i * 0.12}s` }}>
              <div style={{ width: '72px', height: '72px', borderRadius: '50%', background: 'var(--bg3)', border: '2px solid var(--lime)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 20px', color: 'var(--lime)', position: 'relative', zIndex: 1 }}>
                {s.icon}
              </div>
              <div style={{ fontFamily: 'Syne,sans-serif', fontSize: '11px', letterSpacing: '3px', color: 'var(--lime-dark)', marginBottom: '8px', fontWeight: 700 }}>{s.num}</div>
              <h3 style={{ fontFamily: 'Syne,sans-serif', fontSize: '17px', fontWeight: 700, color: 'var(--text)', marginBottom: '10px' }}>{s.title}</h3>
              <p style={{ fontSize: '13px', color: 'var(--text2)', lineHeight: 1.7 }}>{s.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ─── WHO IS IT FOR ────────────────────────────────────────────────────────────
function WhoIsItFor({ width }) {
  const businesses = [
    { emoji: '🍽️', name: 'Restaurants & Cafés' },
    { emoji: '🏥', name: 'Clinics & Doctors' },
    { emoji: '🏫', name: 'Schools & Academies' },
    { emoji: '💈', name: 'Salons & Spas' },
    { emoji: '🛒', name: 'Retail Shops' },
    { emoji: '🏗️', name: 'Construction Firms' },
    { emoji: '🎓', name: 'Tutors & Coaches' },
    { emoji: '🏠', name: 'Real Estate Agents' },
    { emoji: '🚗', name: 'Auto Dealers' },
    { emoji: '🌿', name: 'NGOs & Charities' },
    { emoji: '💼', name: 'Consultants & Lawyers' },
    { emoji: '📸', name: 'Photographers' },
  ];

  return (
    <section className="section" style={{ background: 'var(--bg2)' }}>
      <div className="container">
        <div style={{ textAlign: 'center', marginBottom: '52px' }}>
          <div className="label reveal" style={{ justifyContent: 'center' }}>Made For</div>
          <h2 className="syne reveal" style={{ fontSize: 'clamp(28px,4vw,52px)', fontWeight: 800, letterSpacing: '-1.5px', marginBottom: '16px' }}>
            Perfect for any<br /><span className="lime-text">Pakistani business.</span>
          </h2>
          <p className="reveal" style={{ fontSize: '15px', color: 'var(--text2)', maxWidth: '480px', margin: '0 auto', lineHeight: 1.8 }}>
            If your business doesn't have a website yet, WebWalay was built for you.
          </p>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: width > 1024 ? 'repeat(6, 1fr)' : width > 640 ? 'repeat(3, 1fr)' : 'repeat(2, 1fr)', gap: '12px' }}>
          {businesses.map((b, i) => (
            <div key={i} className="card reveal" style={{ padding: '20px 14px', textAlign: 'center', transitionDelay: `${i * 0.04}s`, cursor: 'default' }}>
              <div style={{ fontSize: '28px', marginBottom: '8px' }}>{b.emoji}</div>
              <div style={{ fontSize: '12px', fontWeight: 600, color: 'var(--text2)', lineHeight: 1.4 }}>{b.name}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ─── TESTIMONIALS ─────────────────────────────────────────────────────────────
function Testimonials({ width }) {
  const reviews = [
    { name: 'Ahmed Raza', biz: 'Raza Medical Center, Lahore', text: 'WebWalay gave our clinic a professional online presence in less than a week. Patients now book appointments through the website. Bilkul asan process tha!', stars: 5 },
    { name: 'Sana Khan', biz: 'Sana\'s Kitchen, Karachi', text: 'I was worried websites would be too expensive for a small restaurant. Rs. 5,000/month is nothing compared to the new customers we get online every day now.', stars: 5 },
    { name: 'Tariq Mahmood', biz: 'Mahmood Real Estate, Islamabad', text: 'Our property listings look so professional now. The team responds super fast on WhatsApp and updates anything we need same day. Highly recommend!', stars: 5 },
    { name: 'Fatima Noor', biz: 'Bright Future Academy, Faisalabad', text: 'We needed to attract more students. Within 2 months of having our website, admissions increased. The monthly maintenance means it always stays updated.', stars: 5 },
    { name: 'Usman Ali', biz: 'Ali Auto Workshop, Rawalpindi', text: 'Never thought a workshop needs a website. WebWalay showed me how many people search online for mechanics. Ab toh rooz naye customer aatey hain!', stars: 5 },
    { name: 'Dr. Zara Hussain', biz: 'Zara Skin Clinic, Multan', text: 'The website looks so premium that patients think we\'re a big hospital! WebWalay\'s team understood exactly what we needed. Worth every rupee.', stars: 5 },
  ];

  return (
    <section id="testimonials" className="section" style={{ background: 'var(--bg)' }}>
      <div className="container">
        <div style={{ textAlign: 'center', marginBottom: '52px' }}>
          <div className="label reveal" style={{ justifyContent: 'center' }}>Reviews</div>
          <h2 className="syne reveal" style={{ fontSize: 'clamp(28px,4vw,52px)', fontWeight: 800, letterSpacing: '-1.5px', marginBottom: '16px' }}>
            Businesses love<br /><span className="lime-text">WebWalay.</span>
          </h2>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: width > 1024 ? 'repeat(3, 1fr)' : width > 640 ? 'repeat(2, 1fr)' : '1fr', gap: '16px' }}>
          {reviews.map((r, i) => (
            <div key={i} className="card reveal" style={{ padding: '24px', transitionDelay: `${i * 0.08}s` }}>
              <div style={{ display: 'flex', gap: '3px', marginBottom: '14px' }}>
                {Array.from({ length: r.stars }).map((_, j) => (
                  <Star key={j} size={14} fill="var(--lime)" color="var(--lime)" />
                ))}
              </div>
              <p style={{ fontSize: '14px', color: 'var(--text2)', lineHeight: 1.75, marginBottom: '16px', fontStyle: 'italic' }}>"{r.text}"</p>
              <div>
                <div style={{ fontFamily: 'Syne,sans-serif', fontSize: '14px', fontWeight: 700, color: 'var(--text)' }}>{r.name}</div>
                <div style={{ fontSize: '12px', color: 'var(--lime-dark)', marginTop: '2px' }}>{r.biz}</div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ─── FAQ ─────────────────────────────────────────────────────────────────────
function FAQ() {
  const [open, setOpen] = useState(null);
  const faqs = [
    { q: 'What exactly do I get for Rs. 5,000/month?', a: 'You get a professionally designed website, reliable Pakistani hosting, free SSL certificate, monthly content updates (text, images, business hours, etc.), basic SEO setup, Google Analytics, and WhatsApp/call support. Everything is included — no hidden fees.' },
    { q: 'How long does it take to launch my website?', a: 'Once you sign up and provide your content (business name, description, photos, etc.), we typically launch within 5-7 working days. Urgent launches can be arranged.' },
    { q: 'Do I own my website?', a: 'The website is built and maintained on our infrastructure as part of the subscription. If you cancel, you can request a copy of your website files. We want to be fully transparent about this.' },
    { q: 'Can I cancel anytime?', a: 'Yes! There are no long-term contracts. You can cancel with 30 days notice. We believe in earning your subscription every month.' },
    { q: 'What if I need changes to my website?', a: 'Monthly updates are included. Need to change your menu, add new products, update phone numbers, or add photos? Just WhatsApp us and we\'ll handle it same day or within 24 hours.' },
    { q: 'Do you work with businesses outside Karachi?', a: 'Absolutely! We serve businesses all across Pakistan — Karachi, Lahore, Islamabad, Faisalabad, Multan, Peshawar, and everywhere in between. Everything is handled remotely.' },
    { q: 'What if I already have a domain name?', a: 'No problem. We can connect your existing domain to your new website. Or we can help you get a new .pk or .com domain as part of your subscription.' },
    { q: 'Is this suitable for a very small business?', a: 'That\'s exactly who we built this for! Whether you\'re a one-person operation or have 50 employees, Rs. 5,000/month makes professional web presence accessible to everyone.' },
  ];

  return (
    <section className="section" style={{ background: 'var(--bg2)' }}>
      <div className="container" style={{ maxWidth: '760px' }}>
        <div style={{ textAlign: 'center', marginBottom: '52px' }}>
          <div className="label reveal" style={{ justifyContent: 'center' }}>FAQ</div>
          <h2 className="syne reveal" style={{ fontSize: 'clamp(28px,4vw,52px)', fontWeight: 800, letterSpacing: '-1.5px' }}>
            Got questions? <span className="lime-text">We got answers.</span>
          </h2>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
          {faqs.map((f, i) => (
            <div key={i} className="card reveal" style={{ overflow: 'hidden', transitionDelay: `${i * 0.05}s` }}>
              <button onClick={() => setOpen(open === i ? null : i)}
                style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '20px 24px', background: 'none', border: 'none', cursor: 'pointer', textAlign: 'left', gap: '16px' }}>
                <span style={{ fontFamily: 'Syne,sans-serif', fontSize: '15px', fontWeight: 700, color: 'var(--text)' }}>{f.q}</span>
                <span style={{ color: 'var(--lime-dark)', flexShrink: 0 }}>{open === i ? <ChevronUp size={18} /> : <ChevronDown size={18} />}</span>
              </button>
              {open === i && (
                <div style={{ padding: '0 24px 20px', fontSize: '14px', color: 'var(--text2)', lineHeight: 1.8, animation: 'fadeUp 0.3s ease' }}>
                  {f.a}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ─── CTA BANNER ──────────────────────────────────────────────────────────────
function CTABanner() {
  return (
    <section style={{ padding: '80px 24px' }}>
      <div style={{ maxWidth: '1140px', margin: '0 auto' }}>
        <div className="reveal-scale" style={{
          background: 'var(--lime)', borderRadius: '24px',
          padding: '64px 48px', textAlign: 'center', position: 'relative', overflow: 'hidden',
        }}>
          <div style={{ position: 'absolute', top: '-80px', right: '-80px', width: '300px', height: '300px', background: 'rgba(255,255,255,0.08)', borderRadius: '50%' }} />
          <div style={{ position: 'absolute', bottom: '-60px', left: '-60px', width: '250px', height: '250px', background: 'rgba(255,255,255,0.08)', borderRadius: '50%' }} />
          <div style={{ position: 'relative' }}>
            <div style={{ fontFamily: 'Syne,sans-serif', fontSize: '11px', letterSpacing: '3px', textTransform: 'uppercase', fontWeight: 700, color: 'rgba(255,255,255,0.6)', marginBottom: '16px' }}>Ready to grow?</div>
            <h2 style={{ fontFamily: 'Syne,sans-serif', fontSize: 'clamp(28px,5vw,56px)', fontWeight: 800, color: '#FFFFFF', letterSpacing: '-1.5px', lineHeight: 1.1, marginBottom: '16px' }}>
              Your business deserves<br />a website. Let's build it.
            </h2>
            <p style={{ fontSize: '16px', color: 'rgba(255,255,255,0.8)', maxWidth: '440px', margin: '0 auto 32px', lineHeight: 1.7 }}>
              Join 500+ Pakistani businesses that already trust WebWalay to represent them online.
            </p>
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'center', flexWrap: 'wrap' }}>
              <Link to="/pricing" style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', padding: '15px 32px', background: '#FFFFFF', color: 'var(--lime-dark)', fontFamily: 'Syne,sans-serif', fontWeight: 700, fontSize: '15px', borderRadius: '8px', textDecoration: 'none', transition: 'all 0.2s' }}
                onMouseEnter={e => { e.currentTarget.style.background = '#F5F3FF'; }}
                onMouseLeave={e => { e.currentTarget.style.background = '#FFFFFF'; }}
              >
                Get Started — Rs. 5,000/mo <ArrowRight size={16} />
              </Link>
              <a href="https://wa.me/923000000000" target="_blank" rel="noreferrer"
                style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', padding: '15px 32px', background: 'rgba(255,255,255,0.15)', color: '#FFFFFF', fontFamily: 'Syne,sans-serif', fontWeight: 600, fontSize: '15px', borderRadius: '8px', textDecoration: 'none' }}>
                💬 Chat on WhatsApp
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

// ─── HOME PAGE ────────────────────────────────────────────────────────────────
export default function Home() {
  const [width, setWidth] = useState(window.innerWidth);

  useEffect(() => {
    const handleResize = () => setWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useReveal();
  return (
    <>
      <Hero width={width} />
      <Stats width={width} />
      <Marquee items={['Website Development', 'Monthly Hosting', 'Content Updates', 'SSL Security', 'Mobile Optimised', 'SEO Ready', 'Google Analytics', 'Pakistani Support', 'No Contracts']} />
      <Problem width={width} />
      <Services width={width} />
      <Marquee items={['Restaurants', 'Clinics', 'Schools', 'Salons', 'Shops', 'NGOs', 'Real Estate', 'Lawyers', 'Tutors', 'Factories', 'Builders', 'Photographers']} reverse speed={25} />
      <Process width={width} />
      <WhoIsItFor width={width} />
      <Testimonials width={width} />
      <FAQ />
      <CTABanner />
    </>
  );
}
